/**
 * Builder model -> YAML conditions node.
 *
 * `builderModelToYamlNode` produces the plain JS value (string | array | object)
 * that, once run through the campaign YAML serializer, reproduces the exact DSL
 * the backend evaluates. `builderModelToText` is a convenience that stringifies
 * that node on its own (used to seed the advanced-YAML editor).
 */

import { stringify } from "yaml";

import {
	BuilderNode,
	ConditionGroup,
	ConditionRule,
	ConditionValue,
	TimeUnit,
	isGroup,
} from "./types";

const pluralizeUnit = (count: number, unit: TimeUnit): string =>
	Math.abs(count) === 1 ? unit : `${unit}s`;

/** Render a right-hand-side value back to its DSL text form. */
export const serializeValue = (value: ConditionValue): string => {
	switch (value.kind) {
		case "literal":
			return value.value;
		case "variable":
			return `{{${value.name}}}`;
		case "datetime":
			return value.iso;
		case "nowRelative": {
			if (value.offset === 0) return "NOW()";
			const sign = value.offset < 0 ? "-" : "+";
			return `NOW() ${sign} ${Math.abs(value.offset)} ${pluralizeUnit(value.offset, value.unit)}`;
		}
		case "varRelative": {
			const sign = value.offset < 0 ? "-" : "+";
			return `${value.varName} ${sign} ${Math.abs(value.offset)} ${pluralizeUnit(value.offset, value.unit)}`;
		}
	}
};

/** Render a single rule to its `"<field> <op> <value>"` string form. */
export const ruleToYamlString = (rule: ConditionRule): string => {
	if (rule.operator === "?" || rule.operator === "!?") {
		return `${rule.fieldName} ${rule.operator}`.trim();
	}
	const rhs = rule.value ? serializeValue(rule.value) : "";
	return `${rule.fieldName} ${rule.operator} ${rhs}`.trim();
};

const groupToYamlNode = (group: ConditionGroup): unknown => {
	const childNodes = group.children
		.map((child) => builderModelToYamlNode(child))
		.filter((node) => node !== undefined);

	if (childNodes.length === 0) return undefined;
	// A single-child group is redundant: emit the child directly.
	if (childNodes.length === 1) return childNodes[0];
	return { [group.kind]: childNodes };
};

/**
 * Convert a builder node to the YAML-serializable conditions value.
 * Returns `undefined` for an empty/null tree so callers can drop the key.
 */
export const builderModelToYamlNode = (node: BuilderNode | null): unknown => {
	if (node === null) return undefined;
	if (isGroup(node)) return groupToYamlNode(node);
	return ruleToYamlString(node);
};

/** Stringify a builder node on its own (for the advanced-YAML editor seed). */
export const builderModelToText = (node: BuilderNode | null): string => {
	const yamlNode = builderModelToYamlNode(node);
	if (yamlNode === undefined) return "";
	return stringify(yamlNode, { lineWidth: 0 });
};
